import { RoadmapItem, StateColor, Project } from '../types';

const VERSION_LABEL_KEYWORDS = [
    'roleplay',
    'abilities',
    'expansion',
    'version',
    'release',
    'v1',
    'v2',
    'v3'
];

export const isVersionLabel = (labelName: string): boolean => {
    const normalizedName = labelName.toLowerCase();
    return VERSION_LABEL_KEYWORDS.some(keyword => normalizedName.includes(keyword));
};

export const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

export const getPriorityLabel = (priority?: number): string => {
    if (!priority) return 'None';
    switch (priority) {
        case 1: return 'Urgent';
        case 2: return 'High';
        case 3: return 'Medium';
        case 4: return 'Low';
        default: return 'None';
    }
};

export const getStateColor = (stateName: string): StateColor => {
    switch (stateName.toLowerCase()) {
        case 'todo': return 'default';
        case 'in progress': return 'primary';
        case 'in review': return 'warning';
        case 'done':
        case 'completed':
        case 'closed': return 'success';
        default: return 'default';
    }
};

export const cleanMarkdown = (markdown: string): string => {
    if (!markdown) return '';
    // Remove image syntax: ![alt text](url) or ![alt text](url "title")
    return markdown.replace(/!\[.*?\]\(.*?\)/g, '');
};

export const getAvailableVersions = (issues: RoadmapItem[]): string[] => {
    const versions = new Set<string>();
    issues.forEach(issue => {
        if (issue.labels?.nodes) {
            issue.labels.nodes.forEach(label => {
                if (isVersionLabel(label.name)) {
                    if (!versions.has(label.name.toLowerCase())) {
                        versions.add(label.name);
                    }
                }
            });
        }
    });

    return Array.from(versions).sort((a, b) => {
        const normalizedA = a.toLowerCase();
        const normalizedB = b.toLowerCase();

        if (normalizedA.includes('roleplay') && !normalizedB.includes('roleplay')) return -1;
        if (!normalizedA.includes('roleplay') && normalizedB.includes('roleplay')) return 1;
        return a.localeCompare(b);
    });
};

export const filterIssuesByVersion = (issueList: RoadmapItem[], versionFilter: string): RoadmapItem[] => {
    if (versionFilter === 'all') return issueList;
    const normalizedFilter = versionFilter.toLowerCase();

    return issueList.filter(issue => {
        if (!issue.labels?.nodes) return false;
        return issue.labels.nodes.some(label => label.name.toLowerCase() === normalizedFilter);
    });
};

export const isMinorProject = (project: Project): boolean => {
    return project.labels?.nodes?.some(
        label => label.name.toLowerCase() === 'minor'
    ) ?? false;
};

const stripMarkdownInline = (text: string): string => {
    return text
        .replace(/!\[.*?\]\(.*?\)/g, '')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/`([^`]+)`/g, '$1')
        .trim();
};

const getProjectTextSources = (project: Project): string[] => {
    const sources = [project.content, project.description]
        .filter((value): value is string => Boolean(value?.trim()));
    return sources;
};

const extractValueFromProjectLines = (project: Project, matcher: RegExp): string | null => {
    const sources = getProjectTextSources(project);
    if (sources.length === 0) return null;

    for (const source of sources) {
        for (const line of source.split(/\r?\n/)) {
            const plain = stripMarkdownInline(line);
            const match = plain.match(matcher);
            if (match?.[1]) {
                const value = match[1].trim();
                if (value) return value;
            }
        }
    }

    return null;
};

export const getProjectedReleaseDate = (project: Project): string | null => {
    return extractValueFromProjectLines(project, /projected\s+release\s*:\s*(.+)/i);
};

export const getProjectVersion = (project: Project): string | null => {
    return extractValueFromProjectLines(project, /version\s*:\s*(.+)/i);
};

export const isProjectDelayed = (project: Project): boolean => {
    const source = project.content || project.description || '';
    return /\bdelayed\b/i.test(source);
};

export const normalizeProjectState = (state: string): string => state.toLowerCase();

export const isReleasedProjectState = (state: string): boolean =>
    normalizeProjectState(state) === 'completed';

export const isInProgressProjectState = (state: string): boolean => {
    const normalized = normalizeProjectState(state);
    return normalized === 'started' || normalized === 'active' || normalized === 'in-progress';
};

export const isInPlanningProjectState = (state: string): boolean =>
    normalizeProjectState(state) === 'planned';

export const getProjectStateLabel = (state: string): string => {
    const normalized = normalizeProjectState(state);
    if (normalized === 'completed') return 'Released';
    if (normalized === 'started' || normalized === 'active' || normalized === 'in-progress') return 'in progress';
    if (normalized === 'planned') return 'in planning';
    return state;
};

export const isReleaseToProgressConnector = (from: Project, to: Project): boolean =>
    isReleasedProjectState(from.state) && isInProgressProjectState(to.state);

export const getDefaultProject = (projects: Project[]): Project | undefined => {
    if (projects.length === 0) return undefined;
    const started = projects.find(project => project.state.toLowerCase() === 'started');
    return started ?? projects[0];
};

export const sortProjects = (projects: Project[]): Project[] => {
    return [...projects].sort((a, b) => {
        const priorityA = a.priority ?? 0;
        const priorityB = b.priority ?? 0;

        // Linear: 0 = no priority (sort last), 1 = urgent … 4 = low
        if (priorityA === 0 && priorityB === 0) return a.name.localeCompare(b.name);
        if (priorityA === 0) return 1;
        if (priorityB === 0) return -1;
        if (priorityA !== priorityB) return priorityA - priorityB;

        return a.name.localeCompare(b.name);
    });
};

export const filterIssuesByProject = (issueList: RoadmapItem[], project: Project): RoadmapItem[] => {
    const normalizedName = project.name.toLowerCase();

    return issueList.filter(issue => {
        if (issue.project?.id === project.id) return true;
        if (!issue.labels?.nodes) return false;
        return issue.labels.nodes.some(label => label.name.toLowerCase() === normalizedName);
    });
};

export const groupIssuesByState = (issues: RoadmapItem[]) => {
    return {
        todo: issues.filter(item => item.state.name === 'Todo'),
        inProgress: issues.filter(item => item.state.name === 'In Progress'),
        inReview: issues.filter(item => item.state.name === 'In Review'),
        done: issues.filter(item => item.state.name === 'Done'),
    };
};

export const calculateCompletionPercentage = (issues: RoadmapItem[]): number => {
    if (issues.length === 0) return 0;
    const completed = issues.filter(issue => issue.state.name === 'Done').length;
    return Math.round((completed / issues.length) * 100);
};
